import { Matrix4, Quaternion, Vector3 } from 'three';

export const EPSILON = 1e-6;
export const TWO_PI = Math.PI * 2;

const scratchMatrix = new Matrix4();
const scratchPerpendicular = new Vector3();
const scratchCross = new Vector3();
const REFERENCE_AXIS = new Vector3(1, 0, 0);
const FALLBACK_AXIS = new Vector3(0, 0, 1);

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/**
 * The fraction of the way from a current value to a target in `dt` seconds,
 * for an exponential (frame-rate independent) smoothing at `rate` per second.
 *
 * `current += (target - current) * smoothFactor(rate, dt)`.
 */
export function smoothFactor(rate: number, dt: number): number {
  return 1 - Math.exp(-Math.max(0, rate) * Math.max(0, dt));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

/**
 * Quintic smoothstep: zero first and second derivatives at both ends, so a
 * blend starts and stops without a visible acceleration step.
 */
export function smootherstep(t: number): number {
  const x = clamp01(t);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

export function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function radToDeg(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Wrap an angle into the (-PI, PI] range. */
export function wrapAngle(angle: number): number {
  let a = (angle + Math.PI) % TWO_PI;
  if (a < 0) a += TWO_PI;
  const wrapped = a - Math.PI;
  // The range is half open, so -PI is reported as PI.
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

/** Shortest signed difference from `from` to `to`, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

/**
 * The signed angle from direction `a` to direction `b`, measured around
 * `axis`. Used to work out how far a body has turned without a heading angle
 * to read it from.
 */
export function signedAngleAround(a: Vector3, b: Vector3, axis: Vector3): number {
  const cosine = Math.min(1, Math.max(-1, a.dot(b)));
  if (cosine > 0.999999) return 0;
  scratchCross.crossVectors(a, axis);
  const sine = scratchCross.dot(b);
  return Math.atan2(sine, cosine);
}

/** Project a vector onto the plane perpendicular to `normal` (normal must be unit length). */
export function projectOnPlane(vector: Vector3, normal: Vector3, out = new Vector3()): Vector3 {
  out.copy(vector).addScaledVector(normal, -vector.dot(normal));
  return out;
}

/** Normalised linear interpolation between two unit vectors. */
export function nlerpVector(a: Vector3, b: Vector3, t: number, out = new Vector3()): Vector3 {
  out.copy(a).lerp(b, t);
  const length = out.length();
  if (length < EPSILON) return out.copy(a);
  return out.divideScalar(length);
}

/**
 * Spherical interpolation between two directions. Unlike a normalised lerp this
 * rotates at a constant angular rate, which matters when a body swings through
 * 90 degrees: a lerp would rush through the middle and stall at the ends.
 */
export function slerpVector(a: Vector3, b: Vector3, t: number, out = new Vector3()): Vector3 {
  const dot = Math.min(1, Math.max(-1, a.dot(b)));
  const theta = Math.acos(dot);
  if (theta < 1e-5) return out.copy(a);
  if (Math.sin(theta) < 1e-6) {
    // The directions are opposite, so there is no unique great circle between
    // them. Pick one and rotate around it.
    scratchPerpendicular.copy(a).cross(REFERENCE_AXIS);
    if (scratchPerpendicular.lengthSq() < 1e-8) scratchPerpendicular.copy(a).cross(FALLBACK_AXIS);
    scratchPerpendicular.normalize();
    return out.copy(a).applyAxisAngle(scratchPerpendicular, theta * t);
  }
  const sinTheta = Math.sin(theta);
  const weightA = Math.sin((1 - t) * theta) / sinTheta;
  const weightB = Math.sin(t * theta) / sinTheta;
  return out.set(
    a.x * weightA + b.x * weightB,
    a.y * weightA + b.y * weightB,
    a.z * weightA + b.z * weightB,
  );
}

/**
 * Three.js is right-handed with objects facing local -Z, so a body's frame is
 *   local +X -> right, local +Y -> up, local +Z -> back.
 * `right` must equal `forward x up` for the basis to stay right-handed.
 */
export function quaternionFromBasis(
  right: Vector3,
  up: Vector3,
  back: Vector3,
  out = new Quaternion(),
): Quaternion {
  scratchMatrix.makeBasis(right, up, back);
  return out.setFromRotationMatrix(scratchMatrix);
}

/** Visual right-hand side for a body: right = forward x up. */
export function rightFromForwardUp(forward: Vector3, up: Vector3, out = new Vector3()): Vector3 {
  return out.crossVectors(forward, up).normalize();
}

/** True when the two unit vectors are parallel (within tolerance). */
export function isParallel(a: Vector3, b: Vector3, tolerance = 1e-4): boolean {
  return Math.abs(a.dot(b)) > 1 - tolerance;
}
